/**
 * =========================================================================================
 * Pricing Plan - Plan Card Component
 * =========================================================================================
 */

import React from 'react';
import * as Icon from 'ubs-icons';
import UButton from 'ubs-framework/v2/UButton';
import { numberWithComaToFixed } from 'ubs-framework/v2/common/function';
import { getTierIcon, getFeatureIcon, getDiscountForCycle, getBillingMultiplier, getBillingCycleShortLabel } from './utils';
import { SUBSCRIPTION_STATUS } from '../../../../../../common/constant';
import { useSelector } from 'react-redux';

const PlanCard = React.memo(({ plan, selectedPlanId, onSelect, onContinue, currency, subscriptionStatus, onResumeSubscription, billingCycle }) => {
    const isSelected = selectedPlanId === plan._id;
    const isPaused = [SUBSCRIPTION_STATUS.PAUSED, SUBSCRIPTION_STATUS.CUSTOMER_PAUSED].includes(subscriptionStatus);
    const isCancelled = [SUBSCRIPTION_STATUS.CANCELLED, SUBSCRIPTION_STATUS.CUSTOMER_CANCELLED].includes(subscriptionStatus);

    const _RESUME_PAYMENT = useSelector((e) => e?.common?.pendingPayment);

    const discountPercent = getDiscountForCycle(plan?.discount, billingCycle);
    const hasDiscount = discountPercent > 0;
    const discountedPrice = hasDiscount ? (plan.price * (100 - discountPercent) / 100)?.toFixed(2) : plan.price;
    const multiplier = getBillingMultiplier(billingCycle);
    const isNotMonthly = multiplier > 1;
    const cycleTotal = discountedPrice * multiplier;
    const cycleLabel = getBillingCycleShortLabel(billingCycle);

    return (
        <div
            key={plan._id}
            className={`u_pricing-plan__card u_col-3 u_mb-20 ${plan.isCurrent ? 'u_pricing-plan__card--selected' : ''} ${plan.isRecommended ? 'u_pricing-plan__card--recommended' : ''} ${plan?.type === 1 ? 'u_pricing-plan__card--disabled' : ''}`}
            onClick={() => plan?.type !== 1 && onSelect(plan._id)}
        >
            <div className="u_pricing-plan__card-inner u_p-24 u_b-light u_flex u_h-full">
                {plan.isRecommended && (
                    <div className="u_pricing-plan__card-ribbon">Recommended</div>
                )}
                <div className="u_flex u_align-start u_content-between">
                    <div className="u_mb-16">
                        {getTierIcon(plan.title)}
                    </div>
                    <div className="u_flex-align">
                        {hasDiscount && (
                            <div className={`u_pricing-plan__card-badge green u_color-green ${plan.badge ? 'u_mr-6' : ''}`}>Save {discountPercent}%</div>
                        )}
                        {plan.badge && (
                            <div className="u_pricing-plan__card-badge u_color-blue">{plan.badge}</div>
                        )}
                    </div>
                </div>

                <div className="u_flex-align u_content-between">
                    <div className="u_pricing-plan__card-name u_text-bold u_color-title u_mb-20">{plan.title}</div>
                </div>
                <p className="u_font u_mb-24">{plan.subtitle || ''}</p>

                <div className="u_bb-dash-light u_mb-20 u_pb-20">
                    <div className="u_flex u_align-baseline">
                        <span className="u_pricing-plan__card-amount u_pr-6">{numberWithComaToFixed(discountedPrice, currency, true)}</span>
                        <p className="u_small-font">/ {plan.billingText}</p>
                    </div>
                    {hasDiscount && (
                        <div className="u_flex-align u_mt-6">
                            <span className="u_font u_color-font" style={{ textDecoration: 'line-through' }}>
                                {numberWithComaToFixed(plan.price, currency, true)}
                            </span>
                            <span className="u_font u_color-green u_fw-600 u_ml-6">
                                {discountPercent}% off
                            </span>
                        </div>
                    )}
                    {isNotMonthly && (
                        <p className="u_font u_mt-6 u_color-blue u_fw-600">
                            ({numberWithComaToFixed(cycleTotal, currency, true)} / user / {cycleLabel} billed)
                        </p>
                    )}
                    <p className="u_font u_mt-10">
                        (Min {plan.minEmployee} Employees)
                    </p>
                </div>

                <div className="u_mb-4">
                    <UButton
                        variant={(plan?.isCurrent && plan?.type !== 1) ? 'white' : 'blue'}
                        type={
                            plan?.isCurrent && plan?.type !== 1
                                ? isPaused
                                    ? 'Resume Subscription'
                                    : isCancelled
                                        ? 'Resubscribe'
                                        : 'Add More seats'
                                : plan?.isCurrent
                                    ? 'Current Plan'
                                    : `Get ${plan.title}`
                        }
                        cn="u_pricing-plan__card-btn u_payment-btn"
                        disabled={plan?.type === 1 || _RESUME_PAYMENT?.action === 'BANK_APPROVAL_PENDING'}
                        style={{ borderRadius: '12px' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (plan?.isCurrent && plan?.type !== 1 && isPaused) {
                                onResumeSubscription();
                                return;
                            }
                            onSelect(plan._id);
                            onContinue(plan._id);
                        }}
                    />
                </div>

                <div className="u_pricing-plan__card-features u_mt-20">
                    {plan?.prevPlan && (
                        <div className="u_pricing-plan__card-features-title u_font u_text-bold u_mb-16 u_color-blue">
                            Everything in {plan.prevPlan}, plus:
                        </div>
                    )}
                    {plan?.features?.map((feature, idx) => (
                        <div key={idx} className="u_pricing-plan__card-feature u_flex-align u_mb-14">
                            <div className="">
                                {getFeatureIcon(feature?.title, isSelected)}
                            </div>
                            <div className="u_flex-align">
                                <span className="u_pricing-plan__card-feature-text u_small-font u_fw-500">{feature?.title}</span>
                                {feature?.limit ? (
                                    <p className="u_small-font u_ml-6">({feature?.limit} Limited)</p>
                                ) : ''}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
});

export default PlanCard;
